import { Search } from "lucide-react";
import { useState } from "react";

import FieldsetHeader from "@/src/components/tasks/fieldset-header";
import { InputWrapper } from "@/src/components/tasks/input-wrapper";
import { EntityMatcher } from "@/src/components/tasks/intake-sections/matchers";
import { Hyperlink } from "@/src/components/typography";
import { type Address } from "@/src/models/address";
import { locationShape, type Location } from "@/src/models/location";

interface GeocodeLookupSectionProps {
  address: Address | null;
  metadata?: any;
  metadataKey?: string; // e.g., "homeAddress", "receivingLocation.address"
  onUpdate?: (key: string, value: any) => void;
  onSave?: () => void;
  onFlagForInaccuracyUpdate?: (key: string, value: any) => void;
  setMetadata?: (metadata: any) => void;
  clearMetadata?: () => void;
}

export default function GeocodeLookupSection({
  address,
  metadata,
  metadataKey = "homeAddress",
  onUpdate,
  onSave,
  onFlagForInaccuracyUpdate,
  setMetadata,
  clearMetadata,
}: GeocodeLookupSectionProps) {
  // If address exists, create a dummy location so EntityMatcher renders a "selected" entity.
  const [currentLocation, setCurrentLocation] = useState<Location | null>(
    address ? { ...locationShape, id: "geocode-lookup", address } : null
  );

  const handleLocationSelect = (location: Location | null) => {
    if (location && onUpdate) {
      // Extract address from the selected location
      const newAddress = {
        ...address,
        line1: location.address?.line1 || "",
        line2: location.address?.line2 || "",
        city: location.address?.city || "",
        state: location.address?.state || "",
        postalCode: location.address?.postalCode || "",
        placeId: location.placeId || location.address?.placeId || null,
      };

      onUpdate("patient.homeAddress", newAddress);

      setCurrentLocation(location);
    }
  };

  const handleEmptyToggle = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    e.stopPropagation();

    setCurrentLocation(null);

    onUpdate?.("patient.homeAddress", null);
  };

  return (
    <div className="space-y-4 mt-4">
      <FieldsetHeader title="Address Lookup" icon={Search}>
        {/* Address Search input */}
        <div className="flex justify-center items-center mb-4 gap-2">
          <div className="w-[70%]">
            <EntityMatcher
              type="location"
              existingEntities={[]} // Empty array since we're using geocoding, not filtering on existing locations
              isLoadingExistingEntities={false}
              entity={currentLocation}
              onEntitySelected={handleLocationSelect}
              showCreateNewOption={false}
            />
          </div>
          <div className="flex items-center gap-2">
            <Hyperlink onClick={handleEmptyToggle} href="#" className="text-xs">
              Empty
            </Hyperlink>
          </div>
        </div>
        {/* Address input fields */}
        <div className="grid grid-cols-1 gap-4 mb-4">
          <InputWrapper
            value={address?.line1 || ""}
            onChange={() => {}} // Empty handler for read-only field
            label="Street Address"
            fieldKey="address.line1"
            onSave={() => {
              clearMetadata?.();

              onSave?.();
            }}
            readOnly={true}
            onFocus={() => setMetadata?.(metadata?.[metadataKey]?.line1_metadata)}
          />
          <InputWrapper
            value={address?.line2 || ""}
            onChange={() => {}} // Empty handler for read-only field
            label="Address Line 2"
            fieldKey="address.line2"
            onSave={() => {
              clearMetadata?.();

              onSave?.();
            }}
            readOnly={true}
            onFocus={() => setMetadata?.(metadata?.[metadataKey]?.line2_metadata)}
          />
          <div className="grid grid-cols-3 gap-4">
            <InputWrapper
              value={address?.city || ""}
              onChange={() => {}} // Empty handler for read-only field
              label="City"
              fieldKey="address.city"
              onSave={() => {
                clearMetadata?.();

                onSave?.();
              }}
              readOnly={true}
              onFocus={() => setMetadata?.(metadata?.[metadataKey]?.city_metadata)}
            />
            <InputWrapper
              value={address?.state || ""}
              onChange={() => {}} // Empty handler for read-only field
              label="State"
              fieldKey="address.state"
              onSave={() => {
                clearMetadata?.();

                onSave?.();
              }}
              readOnly={true}
              onFocus={() => setMetadata?.(metadata?.[metadataKey]?.state_metadata)}
            />
            <InputWrapper
              value={address?.postalCode || ""}
              onChange={() => {}} // Empty handler for read-only field
              label="Zip Code"
              fieldKey="address.postalCode"
              onSave={() => {
                clearMetadata?.();

                onSave?.();
              }}
              readOnly={true}
              onFocus={() => setMetadata?.(metadata?.[metadataKey]?.postalCode_metadata)}
            />
          </div>
        </div>
      </FieldsetHeader>
    </div>
  );
}
